import { CommonModule } from '@angular/common';
import { Injector, NgModule } from '@angular/core';
import { createCustomElement } from '@angular/elements';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { MatLegacyChipsModule } from '@angular/material/legacy-chips';
import { MatLegacyDialogModule } from '@angular/material/legacy-dialog';
import { MatLegacyListModule } from '@angular/material/legacy-list';
import { MatLegacyMenuModule } from '@angular/material/legacy-menu';
import { MatLegacySelectModule } from '@angular/material/legacy-select';
import { MatLegacyTabsModule } from '@angular/material/legacy-tabs';
import { RouterModule } from '@angular/router';
import { AccountFacade } from '@mode/accounts/contract';
import { AccountsUiModule } from '@mode/accounts/ui';
import {
  BadgeComponent,
  ButtonComponent,
  HeaderComponent as CapraHeaderComponent,
  ClickOutsideDirective,
  IconButtonComponent,
  IconComponent,
  LogoButtonComponent,
} from '@mode/capra';
import { FeatureFlag, FeatureFlagsFacade } from '@mode/shared/contract-common';
import { LoaderComponent, SharedUiModule } from '@mode/shared/ui';
import { MetaTags, MetaTagsService, SharedUtilDomModule } from '@mode/shared/util-js';
import { ContextDialogWorkspaceComponent } from './context-dialog/context-dialog-workspace/context-dialog-workspace.component';
import { ContextDialogComponent } from './context-dialog/context-dialog.component';
import { ContextDialogService } from './context-dialog/context-dialog.service';
import { EmailAdminDialogComponent } from './email-admin-dialog/email-admin-dialog.component';
import { EmailAdminDialogService } from './email-admin-dialog/email-admin-dialog.service';
import {
  AUTOCOMPLETE_FILTERS_SELECTOR,
  AutocompleteFiltersComponent,
} from './global-search/autocomplete-filters/autocomplete-filters.component';
import { GlobalSearchComponent } from './global-search/global-search.component';
import { HeaderSearchComponent } from './header-search/header-search.component';
import { HeaderComponent } from './header/header.component';
import { NavAccountMenuComponent } from './nav-account-menu/nav-account-menu.component';
import { NavCollectionsComponent } from './nav-collections/nav-collections.component';
import { NavDefinitionsComponent } from './nav-definitions/nav-definitions.component';
import { NavHomeLinksComponent } from './nav-home-links/nav-home-links.component';
import { NavMenuContainerComponent } from './nav-menu-container/nav-menu-container.component';
import { NavMenuService } from './nav-menu.service';
import { ReportCreationMenuComponent } from './report-creation-menu/report-creation-menu.component';
import { AlgoliaDomainModule } from '@mode/algolia/domain';
import { DatasetDomainModule } from '@mode/dataset/domain';
import { DatasetGoogleDomainModule } from '@mode/dataset/google/domain';

@NgModule({
  imports: [
    CommonModule,
    FormsModule,
    ReactiveFormsModule,
    SharedUiModule,
    AccountsUiModule,
    MatLegacyChipsModule,
    MatLegacyDialogModule,
    MatLegacyListModule,
    MatLegacyMenuModule,
    MatLegacySelectModule,
    MatLegacyTabsModule,
    SharedUtilDomModule,
    RouterModule,
    BadgeComponent,
    ButtonComponent,
    CapraHeaderComponent,
    ClickOutsideDirective,
    IconButtonComponent,
    IconComponent,
    LoaderComponent,
    LogoButtonComponent,
    ReportCreationMenuComponent,
    AlgoliaDomainModule,
    DatasetDomainModule,
    DatasetGoogleDomainModule,
  ],
  declarations: [
    ContextDialogComponent,
    ContextDialogWorkspaceComponent,
    EmailAdminDialogComponent,
    HeaderComponent,
    HeaderSearchComponent,
    NavAccountMenuComponent,
    NavCollectionsComponent,
    NavDefinitionsComponent,
    NavHomeLinksComponent,
    NavMenuContainerComponent,
    GlobalSearchComponent,
  ],
  providers: [ContextDialogService, EmailAdminDialogService, NavMenuService],
  exports: [
    HeaderComponent,
    HeaderSearchComponent,
    NavAccountMenuComponent,
    NavCollectionsComponent,
    NavDefinitionsComponent,
    NavHomeLinksComponent,
    NavMenuContainerComponent,
    ReportCreationMenuComponent,
  ],
})
export class AccountsFeatureHeaderModule {
  constructor(
    accountFacade: AccountFacade,
    meta: MetaTagsService,
    private injector: Injector,
    private window: Window,
    private featureFlagsFacade: FeatureFlagsFacade
  ) {
    const selectedOrg = meta.getTag(MetaTags.OwnerUsername);
    if (selectedOrg) {
      accountFacade.loadCurrentOrg(selectedOrg);
    }

    this.initAutocompleteFilterElement();
  }

  /**
   * Wrap {@link AutocompleteFiltersComponent} into an Angular Element {@link Window.CustomElementConstructor} and
   * register it with the {@link Window.CustomElementRegistry} as a global custom {@link Window.HTMLElement}
   *
   * @author Franklin <franklin.clark@thoughtspot.com>
   */
  initAutocompleteFilterElement() {
    const config = { injector: this.injector };
    const elementConstructor = createCustomElement(AutocompleteFiltersComponent, config);

    // don't redefine
    if (!this.window.customElements.get(AUTOCOMPLETE_FILTERS_SELECTOR)) {
      this.window.customElements.define(AUTOCOMPLETE_FILTERS_SELECTOR, elementConstructor);
    }
  }
}
